import { Avatar, Box, Button, CircularProgress, Dialog, Fade, IconButton, Paper, Stack, Typography, useTheme } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import TimeTravelGraph from "./components/Graph/TimeTravelGraph";
import TimeTravelTrend from "./components/ForecastComponents/TimeTravelTrend";
import ForecastParameters from "./components/ForecastAnalytics/ForecastParameters";
import { useSearchVolume } from "./hooks/useSearchTrends";
import SearchTermRanks from "./components/SearchTerms/SearchTermRanks";
import { useRank } from "./hooks/useRank";
import ProductRank from "./components/RankComponents/ProductRank";
import ProductImpressions from "./components/RankComponents/ProductImpressions";
import TimeTravelSeasonality from "./components/ForecastComponents/TimeTravelSeasonality";
import { useState } from "react";
import { useImpersonation } from "@shared/hooks/useImpersonation";
import PageLayout from "@shared/shell/Layout/PageLayout";
import ProductRankList from "./components/Navigation/ProductRankList";
import { ForecastSourceType } from "@domain/forecast/models/Forecast";
import { Icon, IconSize } from "@shared/icons";
import ForecastGrade from "./components/ForecastAnalytics/ForecastGrade";
import RankSpotlight from "./components/SearchTerms/RankSpotlight";
import { useRanks } from "./hooks/useRanks";

const TimeTravelRank: React.FC = () => {

    const navigate = useNavigate();
    const theme = useTheme();

    const { rankId } = useParams<{ rankId: string }>();

    const { rank, isLoading } = useRank(rankId || '');

    const { searchVolume } = useSearchVolume(rank?.searchTerm || '');

    const [showParameters, setShowParameters] = useState(false);
    const [showSpotlight, setShowSpotlight] = useState(false);

    const { canImpersonate } = useImpersonation();

    const productId = (rank?.listing as any)?.product?.id || '';

    const { ranks } = useRanks(productId)

    const handleRankClick = (searchTerm: string) => {
        setShowSpotlight(false);
        const rank = ranks?.find(rank => rank.searchTerm === searchTerm);
        if (rank) {
            navigate(`/timetravel/ranks/${rank.id}`);
        } else {
            navigate(`/timetravel/search/${searchTerm}`);
        }
    };

    return (
        <PageLayout title="Time Travel Rank" backAction={() => { navigate(-1) }}>

            <Stack direction="column" spacing={6}>

                <Stack direction="row" spacing={2} alignItems="center" borderBottom={1} py={2} borderColor={theme.palette.divider}
                    position="sticky" top={0} bgcolor="white" zIndex={1}>
                    <Stack flex={1} direction="row" alignItems="center" gap={2}>
                        <IconButton onClick={() => setShowSpotlight(true)}>
                            <Icon color={theme.palette.text.primary} icon="Search01" size={IconSize.LARGE} />
                        </IconButton>

                        <Typography variant="h6" color="text.primary"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                display: '-webkit-box',
                            }}
                        >
                            {rank?.searchTerm}
                        </Typography>

                        <ForecastGrade grade={rank?.search?.forecastGrade} />
                    </Stack>

                    {rank?.listing.connection?.connector && (
                        <Avatar variant="rounded" src={rank.listing.connection.connector.image} sx={{ border: 1, width: 56, height: 32, borderColor: 'divider' }} />
                    )}
                </Stack>

                {!rank && isLoading && (
                    <Stack alignItems="center">
                        <CircularProgress />
                    </Stack>
                )}

                {rank && (
                    <>
                        <Stack direction="row" spacing={4}>
                            <ProductImpressions rankId={rank.id} />
                            <ProductRank rankId={rank.id} />
                        </Stack>

                        {rank.listing.connection?.connector && (
                            <>
                                <TimeTravelGraph
                                    sourceType={ForecastSourceType.SEARCH}
                                    source={rank.searchTerm}
                                    connector={rank.listing.connection.connector.type}
                                    historicalData={searchVolume?.map(volume => ({
                                        week: volume.week,
                                        year: volume.year,
                                        value: volume.volume,
                                        anomaly: false
                                    }))}
                                />

                                <Stack direction="row" spacing={2}>
                                    <TimeTravelTrend
                                        sourceType={ForecastSourceType.SEARCH}
                                        source={rank.searchTerm}
                                        connector={rank.listing.connection.connector.type}
                                    />

                                    <TimeTravelSeasonality
                                        sourceType={ForecastSourceType.SEARCH}
                                        source={rank.searchTerm}
                                        connector={rank.listing.connection.connector.type}
                                    />
                                </Stack>
                            </>
                        )}

                        <SearchTermRanks searchTerm={rank.searchTerm} />

                        {rank.listing.connection?.connector?.type && showParameters && (
                            <ForecastParameters sourceType={ForecastSourceType.SEARCH} source={rank.searchTerm} connector={rank.listing.connection.connector.type} />
                        )}

                        {canImpersonate && (
                            <Button size="small" onClick={() => setShowParameters(!showParameters)}>
                                {showParameters ? 'Hide Parameters' : 'Show Parameters'}
                            </Button>
                        )}
                    </>
                )}

            </Stack>

            <Dialog open={showSpotlight} onClose={() => setShowSpotlight(false)} maxWidth="xs" fullWidth>
                <RankSpotlight
                    ranks={ranks ?? []}
                    onSelect={handleRankClick}
                />
            </Dialog>
        </PageLayout>
    );
};

export default TimeTravelRank;
